import { ContactUs } from "@App/Components/ContactUs";
import { BrandFilter } from "@App/Components/Filters";
import { Hero } from "@App/Components/Hero";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { useServices } from "@App/hooks/api/useServices";
import { useHome } from "@App/hooks/useHome";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { CategoryServiceStep } from "@App/Pages/Partner/DHL/CategoryServiceStep";
import { PremiumServiceStep } from "@App/Pages/Partner/DHL/PremiumServiceStep";
import { ServiceCategoryStep } from "@App/Pages/Partner/DHL/ServiceCategoryStep";
import {
  dhlServiceCategories,
  splitIntoCategories,
} from "@App/Pages/Partner/DHL/serviceCategories";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import type { Service } from "@App/types";
import {
  ServiceIsInspection,
  ServiceIsWheelChangeTiresStored,
} from "@App/utils/conditionals/services";
import type { ServiceGroup } from "@App/utils/groupServices";
import { Fragment, type FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    togglePremiumService,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    selectService,
    bevSupport,
    brand,
  } = useHome();

  // The category picked in the first step, if any. Kept here rather than in `useHome` so nothing
  // about DHL's flow leaks into the funnel every other whitelabel shares.
  const [category, setCategory] = useState<ServiceGroup | null>(null);

  const selection = splitIntoCategories(services, dhlServiceCategories);

  useEffect(() => {
    if (currentStep === 0 && service) {
      nextStep();
    }
  }, [service]);

  const locationStepProps = useLocationStepDefinition(onSubmit);

  const serviceName = service
    ? service.pivot.display_name
      ? service.pivot.display_name
      : service.name
    : "";

  const selectCategory = (selected: ServiceGroup): void => {
    setCategory(selected);
    setCurrentStep(1);
  };

  // Clearing the category is load-bearing: `selectService` advances by one step and `Stepper` drops
  // falsy children before it numbers them, so dropping the category step in the same render is what
  // puts the premium services at the index the funnel is about to move to.
  const selectUngroupedService = (selected: Service): void => {
    setCategory(null);
    selectService(selected, true);
  };

  const selectCategorisedService = (selected: Service): void => {
    selectService(selected, true);
  };

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={organization.settings.hero.background_image_url ?? "dhl-banner"}
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={category ? category.label : serviceName}
          >
            <ServiceCategoryStep
              selection={selection}
              loading={loading}
              error={error}
              onSelectCategory={selectCategory}
              onSelectService={selectUngroupedService}
            />
          </Step>
          {category && (
            <Step
              title={"Welchen Reifenservice benötigen Sie?"}
              subtitle={null}
              completedTitle={"Reifenservice: "}
              completedGoBackLinkTitle={serviceName}
            >
              <CategoryServiceStep category={category} onSelectService={selectCategorisedService} />
            </Step>
          )}
          {/* DHL only sells add-ons alongside the wheel change, so every other service skips this
              step entirely.

              Keeping the step mounted while the add-ons load is what lets a wheel change without
              any of them fall through to the location step: `selectService` has already advanced
              onto this index, so the step vanishing on an empty result lands the driver on the
              next one instead of on a blank step.

              This leans on the sub-service list being set before the loading flag clears. It is,
              though not obviously: `useHome` crosses its `useState` names, so this flag is really
              the comfort-service query's — but both queries go out in the same tick with identical
              variables, so Apollo dedupes them into one request and resolves them in call order,
              and the sub-service call is the first one. */}
          {ServiceIsWheelChangeTiresStored(service) &&
            (availableSubservicesLoading || availableSubservices?.length > 0) && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices?.length === 0 ? "Keine Auswahl" : ""} ${premiumServices?.map((selected) => `${selected.name}`)}`}
              >
                <PremiumServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices ?? []}
                  loading={availableSubservicesLoading}
                  toggled={toggled}
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Waehlen Sie Ihre Marke.")}
              completedTitle={"Marke: "}
              completedGoBackLinkTitle={brand ? brand.name : ""}
            >
              <BrandFilter onSubmit={selectBrand} />
            </Step>
          )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              {organization && <Benefits organization={organization} />}
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
